<!-- IF ANYONE AUTO-LINTS THIS TEMPLATE AGAIN I'M GONNA GO SUPER SAIYAN MODE -->
<template>
  <!-- Loading state -->
  <div v-if="loading || adGroupData === null" class="flex flex-col gap-1 items-center justify-center w-full h-full"
  :style="{height: mobileView ? 'auto' : 'calc(100vh - 80px)', minHeight: mobileView ? 'calc(100vh - 80px)' : 'auto'}">
    <BaseLoadingSpinnerCircle :width="24" :height="24" class="text-text-muted" />
    <BaseText type="label" size="sm" class="text-text-muted">
      Crunching ad numbers...
    </BaseText>
    <BaseText size="sm" class="text-text-normal">
      This might take a few seconds.
    </BaseText>
  </div>
  <!-- Content -->
  <div v-else class="w-full flex rounded-t-3xl bg-neutral-50 fade-in" :class="mobileView ? 'flex-col p-2' : 'flex-row overflow-hidden'"
  :style="{
    height: mobileView ? 'auto' : `calc(100vh - ${isPublic ? 90 : 80}px)`, 
    minHeight: mobileView ? 'calc(100vh - 80px)' : 'auto'
  }">
    <!-- TOOLBAR & AD CARD -->
    <div class="flex flex-col flex-grow" :style="{height: mobileView ? 'auto' : 'calc(100vh - 80px)'}">
      <!-- TOOLBAR -->
      <div class="relative w-full flex items-center justify-center" :class="{'px-5 py-4 mt-6': !mobileView}">
        <!-- Center toolbar actions -->
        <div class="actions-shadow flex flex-col gap-2 p-2 bg-white min-w-0 z-50"
        style="border-radius: 14px" :class="mobileView ? 'mx-auto w-full' : 'center-toolbar-actions w-max'">
          <!-- Top Row (Creative ID and Share Button) -->
          <div class="flex items-center gap-1 flex-shrink">
            <!-- External ad link -->
            <div v-if="currentAd.ad_id" class="flex items-center gap-1 pl-1.5 rounded-md min-w-0 flex-shrink">
              <BaseText type="body" size="sm" class="text-text-normal truncate whitespace-nowrap">
                Creative Breakdown ID:
              </BaseText>
              <BaseText type="label" size="sm" class="text-text-muted min-w-0 flex-shrink truncate">
                {{ formatCreativeId(currentAd.creative_id) }}
              </BaseText>
            </div>
            <!-- Vertical line spacer -->
            <div v-if="currentAd.ad_id" class="w-px h-4 bg-neutral-50" :class="mobileView ? 'mx-auto' : 'mx-1'" />
            <!-- Open Ad in Meta Ads Manager -->
            <div v-if="openMetaLinks" class="relative mr-1">
              <button class="open-meta-btn flex items-center py-1 pl-2.5 pr-1 rounded-md flex-shrink-0" style="height: 30px;"
              :class="{'active': showMetaLinks}" @click="showMetaLinks = !showMetaLinks">
                <MetaPlatformIcon />
                <BaseText type="label" size="sm" class="ml-1.5 text-text-muted">
                  Open
                </BaseText>
                <div class="transform transition-transform duration-100 ml-0.5"
                :style="{transform: showMetaLinks ? 'scaleY(-1)' : 'scaleY(1)'}">
                  <ChevronIcon class-name="text-icon-normal" />
                </div>
              </button>
              <!-- Open Meta dropdown -->
              <transition name="fadeDrop">
                <div v-if="showMetaLinks" class="p-0.5 rounded-lg bg-white shadow-md transform absolute left-1/2 z-20"
                style="top: calc(100% + 6px); transform: translateX(-50%);"
                v-on-clickaway="() => showMetaLinks = false">
                  <a :href="openMetaLinks.editor" target="_blank" class="flex items-center gap-1 w-full pl-1.5 py-1.5 pr-2.5 rounded-md hover:bg-background-hover transition-colors">
                    <ExternalLinkIcon class="w-5 h-5 text-icon-normal" />
                    <BaseText type="label" size="sm" class="whitespace-nowrap text-text-muted">
                      Open in Editor
                    </BaseText>
                  </a>
                  <a :href="openMetaLinks.adSet" target="_blank" class="flex items-center gap-1 w-full pl-1.5 py-1.5 pr-2.5 rounded-md hover:bg-background-hover transition-colors">
                    <ExternalLinkIcon class="w-5 h-5 text-icon-normal" />
                    <BaseText type="label" size="sm" class="whitespace-nowrap text-text-muted">
                      Open in Ad Set
                    </BaseText>
                  </a>
                </div>
              </transition>
            </div>
            <!-- Share button -->
            <div class="relative">
              <button class="black-btn-primary flex items-center gap-1.5 pl-1.5 py-1 pr-1 rounded-md tooltip-trigger"
              :class="{'text-text-subdued': shareDisabled, 'text-white': !shareDisabled}"
              @click="handleShareAction" :disabled="shareDisabled">
                <BaseLoadingSpinnerCircle v-if="loadingCopyLink" class="w-5 h-5" />
                <ShareIcon v-else class="w-5 h-5" />
                <BaseText type="label" size="sm">
                  {{ isSharePage ? 'Copy Link' : 'Share' }}
                </BaseText>
                <div v-if="!isSharePage" class="transform transition-transform duration-100"
                :style="{transform: showShareOpts ? 'scaleY(-1)' : 'scaleY(1)'}">
                  <ChevronIcon  :class-name="shareDisabled ? 'text-text-subdued' : 'text-white'" />
                </div>
              </button>
              <!-- Share options dropdown -->
              <transition name="fadeDrop">
                <div v-if="showShareOpts" class="p-0.5 rounded-lg bg-white shadow-md transform absolute left-1/2 z-20"
                style="top: calc(100% + 6px); width: 228px; transform: translateX(-50%);"
                v-on-clickaway="() => showShareOpts = false">
                  <button class="flex items-center gap-1 w-full p-1.5 rounded-md hover:bg-background-hover transition-colors"
                  @click="generateCreativeShare({isInternal: true})">
                    <InternalShareIcon class="w-5 h-5 text-icon-normal" />
                    <BaseText type="label" size="sm" class="whitespace-nowrap text-text-muted">
                      Share with Team
                    </BaseText>
                    <BaseText type="body" size="sm" class="ml-auto text-text-normal">
                      (Internal)
                    </BaseText>
                  </button>
                  <button class="flex items-center gap-1 w-full p-1.5 rounded-md hover:bg-background-hover transition-colors"
                  @click="generateCreativeShare({isInternal: false})">
                    <ExternalLinkIcon class="w-5 h-5 text-icon-normal" />
                    <BaseText type="label" size="sm" class="whitespace-nowrap text-text-muted">
                      Public Share
                    </BaseText>
                    <BaseText type="body" size="sm" class="ml-auto text-text-normal">
                      (External)
                    </BaseText>
                  </button>
                </div>
              </transition>
              <!-- Share disabled tooltip fallback -->
              <transition>
                <div v-if="shareDisabled" class="tooltip-container-bottom text-center px-2 py-1 bg-neutral-800 rounded-md shadow-md cursor-default"
                :style="{'width': '160px'}">
                  <BaseText type="label" size="xs" class="text-white">
                    Only available in reports
                  </BaseText>
                </div>
              </transition>
            </div>
          </div>
          <!-- Bottom Row (Account Info) -->
          <div v-if="adAccountInfo" class="w-full flex items-center p-1.5 rounded-md bg-neutral-25 min-w-0">
            <component :is="adAccountInfo.platformIcon" :width="16" :height="16" class="flex-shrink-0" />
            <BaseText type="label" size="sm" class="account-name text-text-muted pl-2 truncate min-w-0">
              {{ adAccountInfo.name }}
            </BaseText>
            <div v-if="adAccountInfo.businessName" class="flex items-center min-w-0 mr-2">
              <div class="w-0 h-2 border-l border-border-hover mx-3 flex-shrink-0" />
              <BaseText type="label" size="sm" class="text-text-muted truncate min-w-0" style="max-width: 250px;">
                {{ adAccountInfo.businessName }}
              </BaseText>
            </div>
            <BaseTooltipV2
              class="ml-auto"
              style="min-width: 8ch"
              :class="{'flex-shrink-0': showFullAdAccountId}"
              :text="showFullAdAccountId ? 'Hide Account ID' : 'Show Account ID'"
              :position="mobileView ? 'bottom' : 'right'"
              offset="8"
            >
              <BaseText type="body" size="xs" class="account-id cursor-pointer text-text-subdued pl-3 transition-colors hover:text-text-muted" @click.native="showFullAdAccountId = !showFullAdAccountId">
                <span dir="ltr">{{ adAccountInfo.id }}</span>
              </BaseText>
            </BaseTooltipV2>
          </div>
        </div>
      </div>
      <!-- AD CARD -->
      <div ref="advertisement-card-area" class="relative w-full flex-grow min-h-0 overflow-y-scroll scrollbar-hide mt-10" :class="mobileView ? 'py-2.5' : 'py-6'">
        <div class="z-10 h-max mx-auto" :class="{'centered-card-wrapper': !enableAdCardScroll}" style="min-width: 384px;"
        :style="{maxWidth: showIframeFallback ? `${iframeFallbackData.dimensions.width}px` : '384px'}">
          <LibraryCard
            ref="advertisement-card"
            :advertisement="currentAd || advertisement"
            is-lens
            :init-ad-copy-expanded="withAdCopyExpanded"
            in-ad-details-drawer
            all-ad-settings
            no-drawer
            hide-copy-button
            relative-media
            class="w-max"
            style="min-width: 384px;"
            :style="{maxWidth: showIframeFallback ? `${iframeFallbackData.dimensions.width}px` : '384px'}"
            :show-lens-iframe-fallback="showIframeFallback"
            :lens-iframe-fallback-html="iframeFallbackData.html"
            @carouselIndex="carouselIndex = $event"
            @expandCopy="determineAdCardOverflow"
            @loaded-content="determineAdCardOverflow"
          />
        </div>
      </div>
    </div>
    <!-- RIGHT-HAND INFO CARD -->
    <div class="info-card relative flex flex-col mt-1 mr-1 bg-white bg-transparent overflow-y-scroll scrollbar-hide" style="border-radius: 20px"
    :style="{
      height: mobileView ? 'auto' : `calc(100vh - ${isPublic ? 98 : 80}px)`, 
      width: mobileView ? '100%' : '600px'
    }">
      <!-- Tab Switcher -->
      <div class="sticky top-0 left-0 w-full h-16 flex items-center gap-2 p-4 bg-white border-b border-border-normal" 
      style="z-index: 200" :class="mobileView ? 'justify-center' : 'justify-start'">
        <button v-for="navBtn in tabNavigations" :key="navBtn.view"
        class="group px-2 py-1.5 flex items-center gap-1.5 rounded-md transition-colors whitespace-nowrap"
        :class="{'hover:bg-neutral-10': sidebarView !== navBtn.view && !navBtn.isDisabled, 'cursor-not-allowed opacity-75': navBtn.isDisabled}"
        @click="sidebarView = navBtn.view" :disabled="navBtn.isDisabled">
          <BaseText type="label" size="sm" class="transition-colors" :class="`${sidebarView === navBtn.view ? 'text-text-muted' : navBtn.isDisabled ? 'text-text-disabled' : 'text-text-subdued group-hover:text-text-muted'}`">
            {{ navBtn.name }}
          </BaseText>
          <BaseText v-if="navBtn.isDisabled" size="xs" class="text-text-disabled">
            (Soon)
          </BaseText>
          <div v-if="navBtn.isNew" class="px-1.5 py-0.5 rounded-full" :class="`bg-primary-${getTheme}-10`">
            <BaseText type="label" size="xs" :class="`text-primary-${getTheme}-300`">
              New
            </BaseText>
          </div>
          <BaseText v-if="navBtn.view === SIDEVIEW.COMMENTS && fbCommentsCount > 0" type="label" size="xs"
          class="text-text-muted px-1.5 py-0.5 rounded bg-neutral-25 transition-colors" :class="{'group-hover:bg-neutral-50': sidebarView !== navBtn.view}">
            {{ fbCommentsCount }}
          </BaseText>
        </button>
      </div>
      <!-- ===================== INFO CARD VIEWS ======================== -->
      <!-- Date range label -->
      <div v-if="sidebarView === SIDEVIEW.DETAILS || sidebarView === SIDEVIEW.METRICS" class="py-2 px-4">
        <div class="px-2 py-1.5 flex items-center justify-between">
          <div class="flex items-center gap-1.5">
            <CalendarIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ dateRangeLabel.rangeName ?? 'Custom Range' }}
            </BaseText>
          </div>
          <BaseText size="sm" class="text-text-normal">
            {{ dateRangeLabel.range }}
          </BaseText>
        </div>
      </div>

      <!-- ---------------------------- DETAILS VIEW ---------------------------- -->
      <div v-if="sidebarView === SIDEVIEW.DETAILS" class="py-2 px-4 flex flex-col gap-2.5 flex-1">
        <!-- Save to SwipeFile button (do not allow for ads without raw assets) -->
        <div v-if="!showIframeFallback && getUser?.user_id" class="w-full" :class="mobileView ? 'mb-2 col-span-2' : 'col-auto'">
          <div v-if="loadingIsSaved" class="w-full h-9 rounded-md skeleton" />
          <div v-else-if="isLibrarySaved">
            <button class="w-full px-2 py-1.5 rounded-md flex items-center justify-center gap-2 text-white fade-in h-9" style="background-color: #00AB75;"
            @click="navigateToSavedAd">
              <BaseText type="label" size="sm">
                Saved to Swipe File
              </BaseText>
              <OpenExternalIcon class="text-white" />
            </button>
            <div v-if="showSyncAction" class="flex items-center justify-center">
              <button class="flex items-center gap-1.5 mt-1.5" @click="syncWithSwipeFile">
                <BaseLoadingSpinnerCircle v-if="loadingSyncWithSwipeFile" class="w-4 h-4 text-text-subdued" />
                <BaseText size="xs" class="text-text-subdued text-center underline">
                  Sync changes with Swipe File
                </BaseText>
              </button>
            </div>
          </div>
          <SaveAdDropdown
            v-else
            class="text-white rounded-md fade-in"
            colored-by-default
            :advertisement="lensSavePayload"
          />
        </div>
        <div class="flex flex-col border-border-normal border rounded-2xl">
          <div class="flex flex-col gap-1.5 p-4 border-b border-border-normal">
            <!-- Spend cell -->
            <div class="flex items-center gap-1">
              <BaseText type="label" size="sm" class="text-text-muted">
                Spend
              </BaseText>
              <div class="relative">
                <InfoSimpleIcon class="cursor-pointer text-icon-normal tooltip-trigger" :width="20" :height="20" />
                <!-- info tooltip -->
                <transition>
                  <div class="tooltip-container text-center px-2 py-1 bg-neutral-800 rounded-md shadow-md cursor-default" :style="{ 'width': '150px' }">
                    <BaseText type="label" size="xs" class="text-white">
                      Total spend for all ads using this creative
                    </BaseText>
                  </div>
                </transition>
              </div>
            </div>
            <BaseText type="display" size="2xs">
              {{ getIsPrivateMode ? '••••' : groupAdInfo.spend.value }}
            </BaseText>
            <PercentageChangeIndicator
              period-text="since previous period"
              :percent="groupAdInfo.spend.previousPercent"
            />
          </div>
          <!-- Roas cell -->
          <div class="px-4 py-2.5 flex items-center justify-between border-b border-border-normal">
            <div class="flex items-center gap-1">
              <BaseText type="label" size="sm" class="text-text-normal">
                ROAS
              </BaseText>
              <div class="relative">
                <InfoSimpleIcon class="cursor-pointer text-icon-normal tooltip-trigger" :width="20" :height="20" />
                <!-- info tooltip -->
                <transition>
                  <div class="tooltip-container text-center px-2 py-1 bg-neutral-800 rounded-md shadow-md cursor-default" :style="{'width': '130px'}">
                    <BaseText type="label" size="xs" class="text-white">
                      ROAS for all ads using this creative
                    </BaseText>
                  </div>
                </transition>
              </div>
              <BaseText type="heading" size="sm" class="text-text-muted ml-1">
                {{ getIsPrivateMode ? '••••' : groupAdInfo.roas.value }}
              </BaseText>
            </div>
            <PercentageChangeIndicator
              period-text=""
              :percent="groupAdInfo.roas.previousPercent"
            />
          </div>
          <!-- Started running cell -->
          <div class="px-4 py-2.5 flex items-center justify-between border-b border-border-normal">
            <BaseText type="label" size="sm" class="text-text-normal">
              Started Running
            </BaseText>
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ runningDate?.started }} | {{ runningDate?.days }} Days
            </BaseText>
          </div>
          <!-- Status cell -->
          <div class="px-4 py-2.5 flex items-center justify-between border-border-normal">
            <BaseText type="label" size="sm" class="text-text-normal">
              Status
            </BaseText>
            <StatusCell
              v-if="groupAdInfo?.status"
              :is-ad-level="isAdLevel"
              :data="{value: { active: groupAdInfo.status.active, paused: groupAdInfo.status.paused, state: groupAdInfo.status.state}}"
            />
          </div>
        </div>
        <div class="space-y-2 relative">
          <!-- CUSTOM TAGS -->
          <div v-if="!isPublic" class="p-1 rounded-lg bg-background-disabled space-y-1">
            <div class="p-2 flex items-center gap-1">
              <StarredIcon class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted">
                Custom Tags
              </BaseText>
            </div>
            <div class="relative rounded-md py-2 px-2.5 detail-section-container bg-white flex items-center gap-1">
              <LensTags
                :tags="currentAd?.creativeTags"
                always-wrap
                @addingStateChange="onAddingStateChange"
                @removeTag="handleRemoveTag"
                @updateTagSearch="tagSearch = $event"
              />
              <LensTagPopover
                :show="isAddingTag && tagSearch.length > 0"
                :search="tagSearch"
                :selected-tags="currentAd?.creativeTags"
                @selectTag="bindCreativeTag"
              />
            </div>
          </div>
        </div>
        <!-- AI Tags -->
        <div v-if="hasAITags" class="p-1 rounded-lg bg-background-disabled space-y-1 relative">
          <div class="p-2 flex items-center gap-1">
            <MagicBoxIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              AI Tags
            </BaseText>
          </div>
          <!-- List AI Tag type cells and their tags -->
          <div v-for="aiType in Object.keys(adAITags)" :key="aiType"
          class="rounded-md py-2 px-2.5 detail-section-container bg-white">
            <div class="flex items-center gap-1">
              <!--  -->
              <AITagIcon class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted capitalize">
                {{ aiType.replace('_', ' ') }}
              </BaseText>
            </div>
            <div v-if="adAITags[aiType].length > 0" class="flex items-center gap-2 mt-2">
              <div v-for="tag in adAITags[aiType]" :key="tag" class="py-0.5 px-3 rounded-3xl border border-border-normal">
                <BaseText type="label" size="sm" class="text-text-muted">
                  {{ tag }}
                </BaseText>
              </div>
            </div>
            <div v-else class="mt-3 text-text-subdued" type="label" size="xs">
              N/A
            </div>
          </div>
        </div>
        <!-- Breakdown Analytics Graphs -->
        <CreativeBreakdownAnalytics
          class="mt-1"
          :advertisement="currentAd || advertisement"
          :selected-breakdown-metrics="selectedBreakdownMetrics"
          :data-summary="dataSummary"
          :loading-metrics="loadingMetrics"
          @change-metric="handleBreakdownMetricChange"
        />
      </div>

      <!-- ---------------------------- METRICS VIEW ---------------------------- -->
      <div v-if="sidebarView === SIDEVIEW.METRICS" class="px-4 py-2 flex flex-col gap-2.5 flex-1">
        <!-- Foreplay Metrics -->
        <div class="flex flex-col p-1 border border-border-normal rounded-2xl">
          <div class="w-full flex items-center gap-2 px-3 py-2.5">
            <MetricsIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Foreplay Metrics
            </BaseText>
          </div>
          <div v-for="metric in foreplayMetricsList" :key="`fp-metric-${metric.key}`"
          class="w-full flex items-center justify-between px-4 py-2">
            <div class="flex items-center gap-1.5 flex-shrink-0">
              <BaseText type="label" size="sm" class="text-text-normal">
                {{ metric.name }}
              </BaseText>
              <div class="relative">
                <InfoSimpleIcon class="text-icon-normal transition-colors duration-100 hover:text-icon-muted"
                @mouseenter.native="hoveredFpMetricTooltip = metric.key" @mouseleave.native="hoveredFpMetricTooltip = null" />
                <!-- FP Metric Tooltip -->
                <transition>
                  <div v-if="hoveredFpMetricTooltip === metric.key" class="fp-metric-tooltip bg-neutral-malpha-25 rounded-md px-2 py-1.5 w-max cursor-default">
                    <BaseText type="body" size="sm" class="text-white">
                      {{ metric.description }}
                    </BaseText>
                  </div>
                </transition>
              </div>
            </div>
            <div class="flex-grow">
              <PercentileMetricScale
                :value="metric.value"
                :max-bar-width="68"
                use-label-weight
                class="justify-end"
              />
            </div>
          </div>
        </div>
        <!-- Performance Metrics (excludes percentile metrics) -->
        <div class="flex flex-col border-border-normal border rounded-2xl">
          <div class="flex items-center justify-between w-full p-4">
            <BaseText type="label" size="sm" class="text-text-muted">
              Performance Metrics
            </BaseText>
            <!-- Preset dropdown -->
            <BaseSingleDropdown
              v-if="!currentAd?.isDataPublic"
              :trigger-class="'h-8'"
              :selected-obj="localSelectedPreset"
              :options="allPresets || []"
              class="z-30"
              min-width="130px"
              max-width="160px"
              :placeholder="getIsCustomPreset ? 'Custom' : 'Select a preset'"
              empty-state-text="You have no presets"
              :option-label="(opt) => opt.name"
              :selected-label="(opt) => `${opt?.name ?? 'Custom'}`"
              :footer-component="presetDropdownFooter"
              show-scrollbar
              @change="handlePresetChange"
            >
              <template #icon>
                <PresetIcon class="text-icon-normal flex-shrink-0" />
              </template>
            </BaseSingleDropdown>
          </div>
          <!-- Rendered KPIs -->
          <div v-if="kpiList.length > 0" class="relative px-4 mb-2.5 transition-opacity duration-100" :class="loadingMetrics ? 'opacity-60' : 'opacity-100'">
            <!-- Loading state -->
            <transition>
              <div v-if="loadingMetrics" class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <BaseLoadingSpinnerCircle :width="28" :height="28" :duration="1" class="text-icon-muted" />
              </div>
            </transition>
            <div v-for="kpi in kpiList" :key="kpi.key" class="flex items-center justify-between py-1.5">
              <BaseText size="sm" class="capitalize text-text-normal">
                {{ kpi.label }}
              </BaseText>
              <div class="p-1 rounded-md bg-background-normal" :style="[adGroupData?.[kpi.key] !== 0 ? {backgroundColor: getColoredCell(kpi.key).scoreColor} : null]">
                <TableCell
                  :cell-value="adGroupData?.[kpi.key]"
                  :cell-type="getMetricLookup?.[kpi.key]?.type"
                  :color-format="getColoredCell(kpi.key).direction"
                  :metric-direction="getMetricLookup?.[kpi.key]?.metric_direction"
                  :currency="brand?.currency"
                  :hide-tooltip="true"
                />
              </div>
            </div>
          </div>
          <!-- No metrics in preset state -->
          <div v-else class="px-4 text-center mb-2.5">
            <BaseText type="label" size="sm" class="text-text-subdued">
              Preset has no metrics to show
            </BaseText>
          </div>
        </div>
      </div>

      <!-- ---------------------------- TRANSCRIPT VIEW ---------------------------- -->
      <div v-if="sidebarView === SIDEVIEW.TRANSCRIPT" class="flex-grow w-full min-h-0">
        <Transcript
          :advertisement="currentAd"
          :carousel-index="carouselIndex"
          :asset-metadata="assetMetadata"
          :mobile-view="mobileView"
          @playProductIntro="playAdVideoAtTimestamp"
        />
      </div>

      <!-- ---------------------------- COMMENTS VIEW (FB Comments for Lens) ---------------------------- -->
      <div v-if="sidebarView === SIDEVIEW.COMMENTS" class="flex-grow w-full h-full">
        <FBComments
          v-if="currentAd?.ad_id"
          :advertisement="currentAd"
          :lens-id="lensId"
          :fb-comments="comments"
          :loading="commentsLoading"
          @total="adCommentsTotal = $event"
        />
      </div>

      <!-- ========================= AD ACTION BUTTONS FOOTER ========================= -->
      <div class="sticky bottom-0 left-0 h-16 w-full flex items-center py-4 pl-4 border-t border-border-normal bg-white z-50"
      :class="advertisementActions.length > 2 ? 'gap-3' : 'gap-5'">
        <!-- Alternate download (thumbnail / collection) -->
        <button v-if="advertisementActions.includes('download-alt')" class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition"
        :class="loadingAltDownload ? 'opacity-60 cursor-default' : 'opacity-100 hover:bg-neutral-25'"  
        @click="() => {downloadAdvertisementAsset(true)}" :disabled="loadingAltDownload">
          <!-- Loading icon logic -->
          <BaseProgressIndicatorRing v-show="loadingAltDownload && retrievingDownloadData" ref="progress-ring-alt"
          class="text-icon-normal" :radius="10" />
          <BaseLoadingSpinnerCircle v-if="loadingAltDownload && !retrievingDownloadData" class="text-icon-normal" />
          <DownloadIcon v-if="!loadingAltDownload" class="text-icon-normal transition-colors group-hover:text-icon-muted" />
          <!-- Alt download button text -->
          <BaseText type="label" size="sm" class="text-text-muted transition-colors" :class="{'group-hover:text-black': !loadingAltDownload}">
            {{ getAltDownloadLabel() }}
          </BaseText>
        </button>
        <!-- Primary asset download -->
        <div v-if="advertisementActions.includes('download')" class="relative">
          <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors"
          :class="[loadingDownload ? 'opacity-60 cursor-default' : 'opacity-100 hover:bg-neutral-25', {'bg-neutral-25': showDownloadDropdown}]"
          @click="() => {handlePrimaryAction()}" :disabled="loadingDownload">
            <!-- Loading icon logic -->
            <BaseProgressIndicatorRing v-show="loadingDownload && retrievingDownloadData" ref="progress-ring"
            class="text-icon-normal" :radius="10" />
            <BaseLoadingSpinnerCircle v-if="loadingDownload && !retrievingDownloadData" class="text-icon-normal" />
            <DownloadIcon v-if="!loadingDownload" class="text-icon-normal transition-colors group-hover:text-icon-muted" />
            <!-- Download button text -->
            <BaseText type="label" size="sm" class="text-text-muted transition-colors capitalize" :class="{'group-hover:text-black': !loadingDownload}">
              {{ getDownloadLabel() }}
            </BaseText>
          </button>
          <!-- Extra options dropdown (if BaseCarousel with video thumbnail) - NOTE: Not sure if needed? -->
          <transition>
            <div v-if="showDownloadDropdown" v-on-clickaway="() => {showDownloadDropdown = false}"
            class="download-dropdown flex flex-col gap-0.5 p-1 bg-white">
              <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
              @click="handleDropdownSelection('thumbnail')">
                <ImageFormatIcon class="text-icon-normal transition-colors group-hover:text-icon-muted" />
                <BaseText type="label" size="sm" class="text-text-normal transition-colors group-hover:text-text-muted">
                  Thumbnail
                </BaseText>
              </button>
              <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
              @click="handleDropdownSelection('video')">
                <VideoFormatIcon class="text-icon-normal transition-colors group-hover:text-icon-muted" />
                <BaseText type="label" size="sm" class="text-text-normal transition-colors group-hover:text-text-muted">
                  Video
                </BaseText>
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <!-- Hidden video element for loading metadata -->
    <video v-if="['video', 'dco'].includes(currentAd?.type) && (currentAd?.video || currentAd?.cards?.[0]?.video)"
    ref="video" style="display: none;" preload="metadata"
    @loadedmetadata="onVideoMetadataLoaded">
      <source :src="currentAd.video" type="video/mp4">
    </video>
  </div>
</template>

<script>
import LensAPI from '@/api/lens'
import FirebaseAPI from '@/api/firebase'
import _ from 'lodash'
import { mapGetters, mapMutations, mapActions } from 'vuex'
import detectAdBlock from '../../../utils/adblock'
import { sendEventSocket } from '../../../api/sockets'
import JSZip from 'jszip'
import { mixin as clickaway } from 'vue-clickaway2'
import formatLabelString from '../../../utils/lens/formatGraphLabelString'
import { getRelativeMetricScoreColor } from '../../../utils/lens/metricScoreUtil'
import { getMatchingPresetName, formatCustomDateRangeLabel } from '../../../utils/dateUtils'
import { providerKeyMap, platformIconMap } from '@/utils/lens/dataSourceUtils'
import { copyTextAsync } from '@/utils/clipboard'

// Components
import RatingSelector from '../../advertisements/DetailsDrawerComponents/RatingSelector'
import Transcript from '../../advertisements/DetailsDrawerComponents/Transcript.vue'
import Comments from '../../advertisements/DetailsDrawerComponents/Comments'
import FBComments from '../../advertisements/DetailsDrawerComponents/FBComments.vue'
import LibraryCard from '../../library/LibraryCard.vue'
import PercentageChangeIndicator from '../PercentageChangeIndicator.vue'
import TableCell from '../table/cells/TableCell.vue'
import PresetIcon from '../../globals/Icons/LensIcons/PresetIcon.vue'
import PercentileMetricScale from '../PercentileMetricScale.vue'
import LensTags from '../LensTags.vue'
import LensTagPopover from '../LensTagPopover.vue'
import CreativeBreakdownAnalytics from './analytics/creative/CreativeBreakdownAnalytics.vue'
import SaveAdDropdown from '../../advertisements/SaveAdDropdown/SaveAdDropdown.vue'
import PresetDropdownFooter from '../PresetDropdownFooter.vue'

// Icons
import OpenExternalIcon from '../../globals/Icons/OpenExternalIcon.vue'
import ShareIcon from '../../globals/Icons/ShareIcon.vue'
import DownloadIcon from '../../globals/Icons/DownloadIcon.vue'
import VideoFormatIcon from '../../globals/Icons/FilterIcons/VideoFormatIcon.vue'
import ImageFormatIcon from '../../globals/Icons/FilterIcons/ImageFormatIcon.vue'
import InfoSimpleIcon from '../../globals/Icons/InfoSimpleIcon.vue'
import StatusCell from '../table/cells/StatusCell.vue'
import CalendarIcon from '../../globals/Icons/CalendarIcon.vue'
import StarredIcon from '../../globals/Icons/StarredIcon.vue'
import MagicBoxIcon from '../../globals/Icons/LensIcons/MetricIcons/MagicBoxIcon.vue'
import AITagIcon from '../../globals/Icons/AITagIcon.vue'
import MetricsIcon from '../../globals/Icons/LensIcons/MetricsIcon.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import ExternalLinkIcon from '../../globals/Icons/ExternalLinkIcon.vue'
import InternalShareIcon from '../../globals/Icons/InternalShareIcon.vue'
import MetaPlatformIcon from '../../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'

export default {
  name: 'AdInsightsContent',
  components: {
    AITagIcon,
    StarredIcon,
    CalendarIcon,
    Transcript,
    MagicBoxIcon,
    RatingSelector,
    Comments,
    FBComments,
    LibraryCard,
    PercentileMetricScale,
    OpenExternalIcon,
    InfoSimpleIcon,
    PercentageChangeIndicator,
    ShareIcon,
    PresetIcon,
    DownloadIcon,
    TableCell,
    VideoFormatIcon,
    ImageFormatIcon,
    StatusCell,
    MetricsIcon,
    LensTags,
    LensTagPopover,
    ChevronIcon,
    ExternalLinkIcon,
    InternalShareIcon,
    MetaPlatformIcon,
    CreativeBreakdownAnalytics,
    SaveAdDropdown
  },
  mixins: [clickaway],
  props: {
    isAdLevel: {
      type: Boolean,
      default: false
    },
    allAds: {
      type: Array,
      default: () => []
    },
    brand: {
      type: Object,
      default: () => {}
    },
    adGroupData: {
      type: Object,
      default: () => {}
    },
    dataSummary: {
      type: Object,
      default: () => {}
    },
    summaryStats: {
      type: Object,
      default: () => {}
    },
    dateRange: {
      type: Object,
      default: () => {}
    },
    selectedColumns: {
      type: Array,
      default: () => []
    },
    sharedBoard: {
      type: Boolean,
      default: () => false
    },
    sharedBrief: {
      type: Boolean,
      default: () => false
    },
    discovery: {
      type: Boolean,
      default: () => false
    },
    loading: {
      type: Boolean,
      default: () => true
    },
    loadingMetrics: {
      type: Boolean,
      default: () => false
    },
    adCardSavedId: {
      type: String,
      default: ''
    },
    allAdsLoaded: {
      type: Boolean,
      default: () => false
    },
    withAdCopyExpanded: {
      type: Boolean,
      default: () => false
    },
    isChildDrawer: {
      type: Boolean,
      default: () => false
    },
    isPublic: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    const SIDEVIEW = {
      DETAILS: 'details',
      METRICS: 'metrics',
      TRANSCRIPT: 'transcripts',
      COMMENTS: 'comments'
    }
    const tabNavs = [
      {
        name: 'Details',
        view: SIDEVIEW.DETAILS,
        isNew: false,
        isDisabled: false
      },
      {
        name: 'Metrics',
        view: SIDEVIEW.METRICS,
        isNew: false,
        isDisabled: false
      },
      {
        name: 'Transcript',
        view: SIDEVIEW.TRANSCRIPT,
        isNew: false,
        isDisabled: false
      },
      {
        name: 'Comments',
        view: SIDEVIEW.COMMENTS,
        isNew: false,
        isDisabled: this.getMetricsPlatform !== 'meta'
      }
    ]

    return {
      // Static
      tabNavs,
      SIDEVIEW,
      adAITags: {},

      localSelectedPreset: null,

      runningDate: {},

      // Data States
      currentAd: {},
      advertisement: {},
      previousAd: {},
      nextAd: {},
      selectedBoards: [],
      selectedRating: [],
      selectedTags: [],
      createdBy: {},
      niches: [],
      betaUser: false,
      assetMetadata: {},
      comments: [],
      /** Total from API when FBComments self-fetches (lens report); used for badge */
      adCommentsTotal: null,
      carouselIndex: 0,
      selectedBreakdownMetrics: {
        genderAge: 'impressions',
        audienceSegment: {
          left: 'spend',
          right: 'impressions'
        },
        platform: {
          left: 'spend',
          right: 'impressions'
        },
        country: 'spend'
      },
      isLibrarySaved: false,
      savedAdId: '',

      tagSearch: '',
      isAddingTag: false,

      // UI States
      sidebarView: 'details',
      loadingNewAds: false,
      loadingTranscription: false,
      adBlockedDetected: false,
      loadingDelete: false,
      commentsLoading: false,
      commentsLoadedForAdId: null,
      enableAdCardScroll: false,
      showDownloadDropdown: false,
      noCommentsImgSrc: require('../../../assets/images/break-the-silence.png'),
      mobileView: window.innerWidth < 1200,
      hoveredFpMetricTooltip: null,
      loadingCopyLink: false,
      showShareOpts: false,
      showSyncAction: false,
      lensSavePayload: {},
      showFullAdAccountId: false,
      adAccountIdHovered: false,
      showMetaLinks: false,
      presetDropdownFooter: PresetDropdownFooter,

      // Download States
      loadingDownload: false,
      loadingAltDownload: false,
      retrievingDownloadData: false,
      loadingLensAdSave: false,
      loadingIsSaved: true,
      loadingSyncWithSwipeFile: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getTeam']),
    ...mapGetters('MiscModule', ['getTheme', 'getIsPrivateMode']),
    ...mapGetters('LensModule', ['getProviders', 'getColorFormat']),
    ...mapGetters('MetricsModule', ['getMetricsPlatform', 'getMetricLookup', 'getMetrics', 'getPresets', 'getIsCustomPreset', 'getAppliedPreset']),

    isSharePage () {
      return this.$route.name === 'AdsShareLensView' || this.$route.path.startsWith('/share/lens-creative/')
    },
    tabNavigations () {
      return this.tabNavs
        .filter((nav) => !this.isHiddenNav(nav.view))
        .map((nav) => {
          if (nav.view === this.SIDEVIEW.COMMENTS) {
            return { ...nav, isDisabled: this.getMetricsPlatform !== 'meta' }
          }
          return nav
        })
    },
    lensId () {
      return this.$route?.params?.lensId || null
    },
    fbCommentsCount () {
      if (this.lensId != null && this.adCommentsTotal != null) {
        return this.adCommentsTotal
      }
      return this.comments?.length || 0
    },
    allPresets () {
      return [...this.getPresets]
    },
    foreplayMetricsList () {
      const fpCalculatedMetrics = this.getMetrics?.foreplay_metrics?.subSections?.find(sec => sec.sectionId === 'calculated_scores')
      if (!fpCalculatedMetrics) return []
      return fpCalculatedMetrics.metrics.map(metric => ({
        ...metric,
        value: this.adGroupData?.[metric.key]
      }))
    },
    kpiList () {
      const kpiCols = this.localSelectedPreset?.table_columns ?? this.getAppliedPreset.table_columns ?? this.selectedColumns?.map(col => col?.key)
      if (!kpiCols || kpiCols.length === 0) return []
      return kpiCols.filter(kpi => this.getMetricLookup?.[kpi]?.parent_group !== 'foreplay_metrics').map((kpi) => {
        return { label: this.getMetricLookup[kpi]?.name, key: kpi }
      })
    },
    advertisementActions () {
      // The list of action buttons to render at the bottom of the info card
      const actions = []
      if (
        (this.currentAd?.type === 'video' && this.currentAd.thumbnail) ||
        (['carousel', 'dco'].includes(this.currentAd?.type) && this.currentAd?.cards?.length)
      ) {
        actions.push('download-alt')
      }
      if (
        (this.currentAd?.type === 'video' && this.currentAd.video) ||
        (this.currentAd?.type === 'image' && this.currentAd.image) ||
        (this.currentAd?.type === 'carousel' && this.currentAd?.cards?.length) ||
        (this.currentAd?.type === 'dco' && this.currentAd?.cards?.length)
      ) {
        actions.push('download')
      }
      return actions
    },
    getShareLink () {
      return (
        process.env.VUE_APP_BASE_URL +
        '/share/lens-creative/' +
        encodeURIComponent(this.currentAd.id) +
        '?user=' +
        encodeURIComponent(this.getUser.user_id)
      )
    },
    shareDisabled () {
      return !this.currentAd?.isCreativeGroup
    },
    transcriptEnabled () {
      return this.currentAd?.type === 'video'
    },
    commentsEnabled () {
      return !this.discovery && !this.mobileView
    },
    canModifyBoard () {
      return true // This was referenced in the original but not defined in computed
    },
    groupAdInfo () {
      const groupData = this.adGroupData || {}

      if (this.loading || !groupData.previous_period) {
        return {
          spend: { value: '', previousPercent: 0 },
          roas: { value: '', previousPercent: 0 },
          status: { active: 0, paused: 0, inactive: 0, state: 'INACTIVE' }
        }
      }

      const prevSpend = groupData.previous_period.spend
      const spendChangeSince = ((groupData.spend - prevSpend) / prevSpend) * 100

      const prevRoas = groupData.previous_period.roas
      const roasChangeSince = ((groupData.roas - prevRoas) / prevRoas) * 100
      return {
        spend: {
          value: formatLabelString(groupData.spend, 'currency', this.brand?.currency),
          previousPercent: spendChangeSince || 0
        },
        roas: {
          value: formatLabelString(groupData.roas, 'number'),
          previousPercent: roasChangeSince || 0
        },
        status: {
          active: groupData.active_ad_count,
          paused: groupData.paused_ad_count,
          state: groupData.ad_status
        }
      }
    },
    dateRangeLabel () {
      const currentRange = this.dateRange
      if (!currentRange?.start || !currentRange?.end) {
        return { rangeName: 'All Time', range: null }
      } else if (currentRange.isRelativeToToday) {
        return { rangeName: getMatchingPresetName(currentRange), range: formatCustomDateRangeLabel(currentRange.start, currentRange.end) }
      } else {
        return { rangeName: null, range: formatCustomDateRangeLabel(currentRange.start, currentRange.end) }
      }
    },
    hasAITags () {
      const tagEntries = Object.entries(this.adAITags)
      return tagEntries?.length && tagEntries?.some(([_, value]) => value?.length > 0)
    },
    showIframeFallback () {
      return !!(
        this.currentAd.isVideoMissing
        && this.currentAd.iframe
        && (this.getMetricsPlatform !== 'meta' || (this.currentAd.width && this.currentAd.height))
      )
    },
    iframeFallbackData () {
      if (!this.showIframeFallback) return { html: '', dimensions: { width: 0, height: 0 } }

      // Compute the iframe dimensions
      // For non-Meta embeds where we lack asset dimensions, assume a default 9:16 aspect ratio
      const nativeWidth = this.currentAd.width || 1080
      const nativeHeight = this.currentAd.height || 1920
      const aspectFactor = nativeHeight / nativeWidth

      const maxWidth = window.innerWidth - 24 - 640 - 32 // Subtracting padding and sidebar width
      const maxHeight = window.innerHeight - 500 // Subtracting padding and top actions bar height

      let compWidth = Math.max(Math.min(nativeWidth, maxWidth), 384)
      let compHeight = compWidth * aspectFactor
      if (compHeight > maxHeight) {
        compHeight = maxHeight
        compWidth = compHeight / aspectFactor
      }
      // Adjust values to ensure a minimum width of 384px
      if (compWidth < 384) {
        const diffFactor = 384 / compWidth
        compHeight *= diffFactor
        compWidth = 384
      }
      compWidth = Math.round(compWidth)
      compHeight = Math.round(compHeight)

      // Generate the iframe HTML
      let iframeHtml = this.currentAd.iframe
      iframeHtml = iframeHtml.replace(/\bwidth="[^"]*"/i, `width="${compWidth}"`)
      iframeHtml = iframeHtml.replace(/\bheight="[^"]*"/i, `height="${compHeight}"`)

      // For tiktok embed, we expand the iframe src with query params to hide bullshit
      if (this.getMetricsPlatform === 'tiktok') {
        const existingSrc = iframeHtml.match(/src="([^"]*)"/i)?.[1]
        // Check if using the old version of the embed, if yes, rebuild the src
        if (existingSrc.includes('/embed/')) {
          const postId = existingSrc.split('/').pop()
          const params = new URLSearchParams()
          params.append('description', 0)
          params.append('music_info', 0)
          params.append('rel', 0)
          const newSrc = `https://www.tiktok.com/player/v1/${postId}?${params.toString()}`
          iframeHtml = iframeHtml.replace(/src="([^"]*)"/i, `src="${newSrc}"`)
        }
      }

      return {
        html: iframeHtml,
        dimensions: {
          width: compWidth,
          height: compHeight
        }
      }
    },
    adAccountInfo () {
      const { account_id, account_name } = this.adGroupData
      if (!account_id || !account_name) return null

      const cleanedId = account_id.replace('act_', '')
      const id = this.showFullAdAccountId ? cleanedId : '••••••••' + cleanedId.slice(-4)
      
      const accountInfo = {
        id,
        name: account_name,
        platformIcon: platformIconMap[this.getMetricsPlatform]
      }
      // If the user has access to providers, extend with the business name of the account
      const accountProvider = this.getProviders?.find(provider => provider.advertisers?.some(adAccount => adAccount.id === account_id))
      if (accountProvider) {
        accountInfo.businessName = accountProvider.business_name
      }
      return accountInfo
    },
    openMetaLinks () {
      if (this.getMetricsPlatform !== 'meta' || !this.adGroupData?.ad_id || !this.adGroupData?.account_id) return null
      const { account_id, ad_id } = this.adGroupData
      const cleanedAccountId = account_id.replace('act_', '')
      return {
        editor: `https://adsmanager.facebook.com/adsmanager/manage/ads/edit?act=${cleanedAccountId}&selected_ad_ids=${ad_id}`,
        adSet: `https://adsmanager.facebook.com/adsmanager/manage/ads?act=${cleanedAccountId}&selected_ad_ids=${ad_id}`
      }
    }
  },
  watch: {
    allAds () {
      this.loadingNewAds = false
    },
    allAdsLoaded () {
      this.loadingNewAds = false
    },
    sidebarView (newView) {
      // When lensId is set, FBComments fetches on mount; otherwise parent lazy-loads
      if (newView === this.SIDEVIEW.COMMENTS && this.commentsEnabled && !this.lensId) {
        const currentAdId = this.currentAd?.ad_id
        if (currentAdId && this.commentsLoadedForAdId !== currentAdId) {
          this.fetchAdComments()
        }
      }
    },
    carouselIndex () {
      const card = this.$refs['advertisement-card']
      if (card) {
        card.setCarouselIndex(this.carouselIndex)
      }
      if (this.sidebarView === 'transcript' && !this.transcriptEnabled) {
        this.sidebarView = 'details'
      }
      this.$nextTick(() => { this.determineAdCardOverflow() })
    },
    async loading (isLoading) {
      if (!isLoading) {
        await this.initInsights()
      }
    },
    adGroupData () {
      this.initInsights({ ignorePresetChange: true })
    },
    currentAd: {
      async handler (newValue, oldValue) {
        // When navigating through ads, we may need to update the sidebar view
        if (
          (this.sidebarView === 'transcript' && !this.transcriptEnabled) ||
          (this.sidebarView === 'comments' && !this.commentsEnabled) ||
          (this.sidebarView === 'ai-analysis' && !this.aiAnalysisEnabled)
        ) {
          this.sidebarView = 'details'
        }

        this.carouselIndex = 0

        // Reset comments cache when switching ads
        if (newValue?.ad_id !== oldValue?.ad_id) {
          this.comments = []
          this.commentsLoadedForAdId = null
          this.adCommentsTotal = null
          // When lensId is set, FBComments self-fetches; otherwise parent fetches
          if (this.commentsEnabled && !this.lensId) {
            this.fetchAdComments()
          }
        }

        // Extract metadata from ad asset
        if (this.currentAd?.type === 'image' && this.currentAd.image) {
          const image = new Image()
          image.src = this.currentAd.image
          if (image.complete) { // In case the image was previously cached
            this.assetMetadata = { width: image.width, height: image.height }
          } else { // Otherwise, wait for the image to load
            image.onload = () => {
              this.assetMetadata = { width: image.width, height: image.height }
            }
          }
        } else if (
          (this.currentAd?.type === 'video' && this.currentAd.video) ||
          (this.currentAd?.type === 'dco' && this.currentAd?.cards?.length && this.currentAd.cards[0].video)
        ) {
          this.$nextTick(() => {
            const video = this.$refs.video
            video.pause()
            video.src = ''
            video.src = this.currentAd.video || this.currentAd.cards[0].video
            video.load()
            // The metadata computations are perfomed when the <video> element emits the 'loadedmetadata' event
          })
        }

        this.$nextTick(() => { this.determineAdCardOverflow() })

        if (newValue.id && newValue.id !== oldValue.id) {
          sendEventSocket('adViewed', { adId: this.currentAd.id, brandId: this.currentAd.brandId })
        }
      },
      immediate: true
    }
  },
  async mounted () {
    // Configure listener for mobile view
    this.mediaQuery = window.matchMedia('(max-width: 1100px)')
    this.mobileView = this.mediaQuery.matches
    this.mediaQuery.addEventListener('change', this.handleBreakpointChange)

    if (!this.loading) {
      await this.initInsights()
      console.log('group data', this.adGroupData)
    }
  },
  beforeDestroy () {
    this.mediaQuery.removeEventListener('change', this.handleBreakpointChange)
  },
  methods: {
    ...mapActions('MetricsModule', ['fetchPresets']),
    ...mapMutations('AuthModule', ['SET_USER']),
    ...mapMutations('AdvertisementsModule', ['SET_AD_PLAYING']),
    ...mapMutations('MiscModule', ['SET_PREVENT_DRAWER_ESCAPE_KEY']),
    detectAdBlock,
    isHiddenNav (view) {
      if (view === this.SIDEVIEW.TRANSCRIPT) {
        return !this.transcriptEnabled
      }
      if (view === this.SIDEVIEW.COMMENTS) {
        return this.getMetricsPlatform !== 'meta'
      }
      return false
    },
    async initInsights (options = {}) {
      this.SET_AD_PLAYING('')
      const groupData = this.adGroupData
      const aiData = groupData.ai_transaction
      const transSegments = aiData.transcription?.segments
      const transcript = transSegments?.length > 0 ? transSegments.map((seg) => {
        return {
          startTime: seg.start,
          endTime: seg.end,
          sentence: seg.text
        }
      }) : []
      this.currentAd = {
        ...groupData,
        name: this.brand.name,
        image: groupData.ad_image_url,
        description: groupData.ad_copy,
        link_url: groupData.destination_url,
        avatar: this.brand?.avatar,
        cta_text: _.startCase(groupData.creative_cta_type.toLowerCase().replace('_', ' ')),
        video: groupData.video_url_source,
        type: this.getMediaType(groupData),
        timestampedTranscription: transcript,
        isVideoMissing: groupData.ad_type === 'video' && !groupData.video_url_source,
        fallbackVideo: groupData?.video_url,
        isLens: true
      }

      this.initLensSavePayload()
      this.runningDate = {
        started: new Date(this.currentAd.min_event_date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).replace(/(\d+)(,)/, '$1th,'),
        days: this.getDayDifference(this.currentAd?.min_event_date, this.currentAd?.max_event_date)
      }
      this.previousAd = this.allAds[(this.allAds.indexOf(this.advertisement) - 1)]
      this.nextAd = this.allAds[(this.allAds.indexOf(this.advertisement) + 1)]

      // AD AI Tags
      const themeTags = Array.isArray(aiData?.theme) ? aiData?.theme : [aiData?.theme]
      const angleTags = Array.isArray(aiData?.ad_angle) ? aiData?.ad_angle : [aiData?.ad_angle]
      const funnelStageTags = Array.isArray(aiData.funnel_stage) ? aiData.funnel_stage : [aiData.funnel_stage]

      this.adAITags = {
        themes: themeTags?.filter(tag => tag) ?? [],
        angles: angleTags?.filter(tag => tag) ?? [],
        funnel_stage: funnelStageTags?.filter(tag => tag) ?? []
      }

      // Fetch presets
      if (this.getPresets?.length === 0) {
        await this.fetchPresets()
      }

      if (!options.ignorePresetChange) {
        this.localSelectedPreset = (
          this.getIsCustomPreset && !this.isAdLevel
            ? (this.selectedColumns?.length > 0 ? { table_columns: this.selectedColumns.map(col => col.key) } : null)
            : (this.getAppliedPreset ?? null)
        ) ?? this.getPresets?.[0]
      }

      if (!this.showIframeFallback) {
        await this.checkIfSaved()
        this.loadingIsSaved = false
      }
    },
    initLensSavePayload () {
      const metricsToPublisherPlatformMap = {
        'meta': 'facebook',
        'tiktok': 'tiktok',
        'linkedin': 'linkedin'
      }
      
      let payload = {
        board_ids: [],
        lensId: this.brand?.id,
        name: this.brand?.name || 'Lens Ad',
        createdAt: new Date().getTime(),
        created_by: this.getUser?.user_id,
        type: this.currentAd.ad_type,
        save_source: 'LENS_CREATIVE',
        ad_id: this.currentAd.ad_id,
        avatar: this.brand?.avatar,
        cta_text: this.currentAd?.cta_text || '',
        cta_type: this.currentAd?.cta_type || '',
        description: this.currentAd?.description || '',
        persona: {
          gender: this.currentAd?.ai_transaction?.persona?.gender || 'unknown',
          age: this.currentAd?.ai_transaction?.persona?.age || 'unknown'
        },
        emotionalDrivers: this.currentAd?.ai_transaction?.emotional_driver || {},
        productCategory: this.currentAd?.ai_transaction?.product_category || 'unknown',
        creativeTargeting: this.currentAd?.ai_transaction?.creative_targeting || {},
      }

      // Add the platform information
      if (metricsToPublisherPlatformMap[this.getMetricsPlatform]) {
        payload.publisher_platform = [metricsToPublisherPlatformMap[this.getMetricsPlatform]]
      }

      if (this.currentAd?.type === 'video') {
        payload.video = this.currentAd.video
        const transcript = (this.currentAd?.ai_transaction?.transcription?.segments || []).map((seg) => ({
          startTime: seg.start,
          endTime: seg.end,
          sentence: seg.text
        }))
        const video = this.currentAd.video
        payload = {
          ...payload,
          video,
          timestampedTranscription: transcript,
          transcriptionStatus: transcript.length > 0 ? 'complete' : 'incomplete',
          needsTranscription: transcript.length === 0 && this.currentAd?.ad_type === 'video'
        }
      } else if (this.currentAd?.type === 'image') {
        payload.image = this.currentAd.image
      }
      this.lensSavePayload = payload
    },
    async checkIfSaved () {
      try {
        const adId = this.currentAd.ad_id
        const { isSaved, id, data } = await FirebaseAPI.Advertisements.checkIfSaved(adId) || {}
        this.isLibrarySaved = isSaved
        this.savedAdId = id

        const transcriptChange = data?.timestampedTranscription?.length !== this.currentAd?.ai_transaction?.transcription?.segments?.length
        const emotionalDriversChange = Object.keys(data?.emotionalDrivers || {}).length !== Object.keys(this.currentAd?.ai_transaction?.emotional_driver || {}).length
        this.showSyncAction = transcriptChange || emotionalDriversChange
      } catch (e) {
        console.error('Error checking if ad is saved', e)
      }
    },
    async syncWithSwipeFile () {
      this.loadingSyncWithSwipeFile = true
      try {
        const { ad_id, board_ids, createdAt, created_by, lensId, save_source, video, name, avatar, cta_text, cta_type, type, ...updatePayload } = this.lensSavePayload
        await FirebaseAPI.Advertisements.update(this.savedAdId, updatePayload)
        this.$showAlert({
          message: 'Updated Lens Ad in Swipe File!',
          type: 'success'
        })
      } catch (e) {
        console.error('Error syncing with swipe file', e)
      } finally {
        this.loadingSyncWithSwipeFile = false
      }
    },
    navigateToSavedAd () {
      window.open(`/library?ad=${this.savedAdId}`, '_blank')
    },
    async handleShareAction () {
      if (!this.isSharePage) {
        this.showShareOpts = !this.showShareOpts
      } else {
        await navigator.clipboard.writeText(window.location.href)
        this.$showAlert({
          message: 'Copied share link to clipboard!',
          type: 'success'
        })
        this.loadingCopyLink = false
      }
    },
    handlePresetChange (preset) {
      this.localSelectedPreset = preset
      if (preset?.table_columns?.length) {
        this.$emit('refetch-insights', preset?.table_columns || [])
      }
    },
    handleBreakdownMetricChange (data) {
      const { breakdownKey, value } = data
      this.selectedBreakdownMetrics[breakdownKey] = value
      const additionalColumns = convertBreakdownMetricsToFlatArray(this.selectedBreakdownMetrics)
      this.$emit('refetch-insights', additionalColumns)
    },
    getMediaType (data) {
      return data?.video_url_source ? 'video' : 'image'
    },
    showColoredCell: _.memoize(function (percentDiff, kpi, outlierFormat) {
      const metricDir = this.getMetricLookup?.[kpi]?.metric_direction
      return getRelativeMetricScoreColor(percentDiff, kpi, outlierFormat, this.summaryStats, metricDir)
    }, (arg1, arg2, arg3) => `${arg1}-${arg2}-${arg3}`),
    getColoredCell (kpi) {
      if (!this.summaryStats) return ''
      const percentDiff = Math.floor(this.adGroupData.percent_deviation?.[kpi])
      return this.showColoredCell(percentDiff, kpi, this.getColorFormat)
    },
    formatValue (value, type) {
      if (!value && value !== 0) return '-'

      if (type === 'currency') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(value)
      } else if (type === 'percent') {
        return `${value?.toFixed(2)}%`
      } else {
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          maximumFractionDigits: 2
        }).format(value)
      }
    },
    async handleUpdateAdRating (rating) {
      // TODO
    },
    onAddingStateChange (isAdding) {
      if (!isAdding) {
        this.tagSearch = ''
      }
      this.isAddingTag = isAdding
    },
    // Sync the tag changes with local table data state
    syncTableResults (key, value) {
      this.$emit('updateTableResults', {
        findKey: this.currentAd.group_by,
        key,
        value
      })
    },
    async bindCreativeTag (tag) {
      const oldTags = this.currentAd?.creativeTags || []
      try {
        // Remember: Is always ad level
        this.currentAd.creativeTags = [...oldTags, tag]
        this.syncTableResults('custom_tags', this.currentAd.creativeTags)
        const hashesToBind = [{ creative_hash: this.currentAd.creative_hash, tag_id: tag?.tag_id || tag?.id, lens_id: this.$route.params.lensId }]

        const { data } = await LensAPI.Tags.tagCreative(hashesToBind)
        console.log('bind tag', data?.id)
      } catch (error) {
        console.error('Error binding tag to creative', error)
        this.currentAd.creativeTags = oldTags
        this.syncTableResults('custom_tags', oldTags)
        this.$showAlert({ type: 'error', message: 'Error binding tag to creative' })
      }
    },
    async handleRemoveTag (tag) {
      const oldTags = this.currentAd?.creativeTags || []
      // Update the local state first & rectify if error
      try {
        this.currentAd.creativeTags = oldTags.filter(t => t.tag_id !== tag.tag_id)
        this.syncTableResults('custom_tags', this.currentAd.creativeTags)
        const tagsToRemove = [{ creative_hash: this.currentAd.creative_hash, tag_id: tag.tag_id, lens_id: this.$route.params.lensId }]
        const { data } = await LensAPI.Tags.unTagCreative(tagsToRemove)
        console.log('remove tag', data?.id)
      } catch (error) {
        console.error('Error removing tag from creative', error)
        this.currentAd.creativeTags = oldTags
        this.syncTableResults('custom_tags', oldTags)
        this.$showAlert({ type: 'error', message: 'Error binding tag to creative' })
      }
    },
    handleUpdateAdBoardsLocal (boardIds) {
      const copyAd = { ...this.currentAd, board_ids: boardIds }
      this.$emit('updateAds', [copyAd])
      this.currentAd = copyAd
    },
    async nextAdvertisement () {
    },
    async previousAdvertisement () {
    },
    async getCreatedByUser () {
    },
    async getNiches () {
    },
    async fetchAdComments () {
      const lensId = this.$route.params.lensId
      const adId = this.currentAd?.ad_id

      if (!lensId || !adId) {
        this.comments = []
        this.commentsLoadedForAdId = null
        return
      }

      this.commentsLoading = true
      try {
        const response = await LensAPI.Lens.getAdComments(lensId, adId)
        
        // Extract comments array from response (handle different possible structures)
        let commentsArray = []
        if (Array.isArray(response)) {
          commentsArray = response
        } else if (response?.comments && Array.isArray(response.comments)) {
          commentsArray = response.comments
        } else if (response?.data && Array.isArray(response.data)) {
          commentsArray = response.data
        }

        // Map backend response to expected format
        this.comments = commentsArray.map(comment => {
          // Parse timestamp - handle different formats
          let timestamp = Date.now()
          if (comment.created_time) {
            const parsed = new Date(comment.created_time)
            if (!isNaN(parsed.getTime())) {
              timestamp = parsed.getTime()
            } else if (typeof comment.created_time === 'number') {
              // Might be Unix timestamp in seconds (not ms)
              timestamp = comment.created_time < 10000000000 
                ? comment.created_time * 1000 
                : comment.created_time
            }
          }
          
          return {
            authorName: comment.author_name || comment.authorName || comment.from?.name || 'Private User',
            authorAvatar: comment.author_avatar || comment.authorAvatar || null,
            text: comment.text || comment.message || '',
            timestamp,
            likeCount: comment.like_count ?? comment.likeCount ?? 0,
            userPostLike: comment.user_post_like ?? comment.userPostLike ?? false
          }
        })
        
        // Track that we've loaded comments for this ad
        this.commentsLoadedForAdId = adId
      } catch (error) {
        console.error('Error fetching ad comments:', error)
        this.comments = []
        this.commentsLoadedForAdId = null
      } finally {
        this.commentsLoading = false
      }
    },
    getDayDifference (start, end) {
      if (!start || !end) return 0
      const startDate = new Date(start)
      const endDate = new Date(end)
      const differenceInTime = endDate - startDate
      return Math.ceil(differenceInTime / (1000 * 60 * 60 * 24))
    },
    // ================== DOWNLOAD METHODS ==================
    handlePrimaryAction () {
      if (
        ['carousel', 'dco', 'dpa'].includes(this.currentAd?.type) &&
        this.currentAd?.cards[this.carouselIndex].thumbnail
      ) {
        this.showDownloadDropdown = !this.showDownloadDropdown
      } else {
        this.downloadAdvertisementAsset()
      }
    },
    handleDropdownSelection (type) {
      const animateProgress = this.$refs['progress-ring'].updateProgress
      const filename = `${this.currentAd.type}-${this.carouselIndex}-${type}-${this.currentAd.id}`
      const url = type === 'thumbnail'
        ? this.currentAd.cards[this.carouselIndex].thumbnail
        : this.currentAd.cards[this.carouselIndex].video
      this.showDownloadDropdown = false
      this.executeDownload(url, filename, animateProgress).catch((error) => {
        console.log('Asset fetch request was rejected', error)
        this.executeBackupDownload(url, error)
      })
    },
    async downloadAdvertisementAsset (alternate = false) {
      // Reset progress states and flag loading
      this.targetDownloadProgress = 0
      this.renderedDownloadProgress = 0
      alternate ? this.loadingAltDownload = true : this.loadingDownload = true

      // Get the progress animation function
      const refStr = alternate ? 'progress-ring-alt' : 'progress-ring'
      const animateProgress = this.$refs[refStr].updateProgress

      // Configure the download based on the advertisement and type
      let url = null
      let filename = null

      try {
        if (this.currentAd?.type === 'image') {
          filename = this.currentAd.ad_name ?? `image-${this.currentAd.id}`
          url = this.currentAd.image
        } else if (this.currentAd?.type === 'video') {
          filename = this.currentAd.ad_name ?? `${alternate ? 'thumbnail' : 'video'}-${this.currentAd.id}`
          url = alternate ? this.currentAd.thumbnail : this.currentAd.video
        }

        if (!url) {
          this.$showAlert({
            type: 'error',
            message: 'Asset type not supported'
          })
          return
        }

        await this.executeDownload(url, filename, animateProgress)
      } catch (error) {
        console.error(error)
        this.executeBackupDownload(url, error)
      } finally {
        alternate ? this.loadingAltDownload = false : this.loadingDownload = false
      }
    },
    async executeDownload (url, filename, animateProgress) {
      // Fetch the asset
      const response = await fetch(url, { mode: 'cors' })
      // Get the content length to track download progress
      const contentLength = response.headers.get('Content-Length')
      if (!response.body || !contentLength) {
        throw new Error('Readable steams or content length not supported')
      }

      this.retrievingDownloadData = true

      // Create a readable stream
      const totalBytes = parseInt(contentLength, 10)
      let loadedBytes = 0
      const reader = response.body.getReader()
      const stream = new ReadableStream({
        start: async (controller) => {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            loadedBytes += value.byteLength
            animateProgress(loadedBytes / totalBytes)

            controller.enqueue(value)
          }
          controller.close()
        }
      })

      const blob = await new Response(stream).blob()
      this.retrievingDownloadData = false

      // Create a downloadable URL for the fetched asset
      const downloadUrl = URL.createObjectURL(blob)
      const fileExtension = url.split('.').pop()
      const fullFilename = `${filename}.${fileExtension}`
      this.triggerDownload(downloadUrl, fullFilename)
    },
    async executeCollectionDownload (assets, filename, animateProgress) {
      // Assets are expected to be an object array with image or video keys

      // Prepare the jszip instance and progress variables
      const zip = new JSZip()
      let totalBytes = 0
      let loadedBytes = 0

      // First we fetch the content length for each asset to track download progress
      const assetSizes = await Promise.all(
        assets.map(async (asset) => {
          const url = asset.image || asset.video
          const response = await fetch(url, { method: 'HEAD', mode: 'cors' })
          return parseInt(response.headers.get('Content-Length'), 10)
        })
      )
      totalBytes = assetSizes.reduce((acc, size) => acc + size, 0)

      this.retrievingDownloadData = true

      // Fetch and add each asset to the zip file
      await Promise.all(
        assets.map(async (asset, index) => {
          const url = asset.image || asset.video
          const response = await fetch(url, { mode: 'cors' })
          if (!response.body) {
            throw new Error('Readable steams not supported')
          }

          const reader = response.body.getReader()
          const fileExtension = url.split('.').pop()
          const fullFilename = `${index}-${filename}.${fileExtension}`
          const fileData = []

          // Create a readable stream
          const stream = new ReadableStream({
            async start (controller) {
              while (true) {
                const { done, value } = await reader.read()
                if (done) break

                fileData.push(value)
                loadedBytes += value.byteLength
                animateProgress(loadedBytes / totalBytes)
                controller.enqueue(value)
              }
              controller.close()
            }
          })

          const blob = await new Response(stream).blob()
          const arrayBuffer = await blob.arrayBuffer()
          zip.file(fullFilename, arrayBuffer)
        })
      )
      this.retrievingDownloadData = false

      // Generate the zip file and download it
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const zipUrl = URL.createObjectURL(zipBlob)
      this.triggerDownload(zipUrl, `${filename}.zip`)
    },
    async executeBackupDownload (url, error) {
      console.log('Attempting backup download')

      if (!url) {
        console.error('Download failed:', error)
        this.$showAlert({
          message: 'Failed to download asset',
          type: 'error'
        })
        return
      }

      // Open the download url in a new tab
      window.open(url, '_blank')
    },
    triggerDownload (url, downloadFilename) {
      // Create a hidden anchor element to trigger the download
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = downloadFilename
      anchor.addEventListener('click', (event) => { event.stopPropagation() })
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      // Revoke the URL to free up memory
      URL.revokeObjectURL(url)
    },

    generateCreativeShare (opts) {
      this.loadingCopyLink = true
      this.showShareOpts = false
      
      copyTextAsync(async () => {
        const { isInternal } = opts
        const isPublic = !isInternal
        const preset = this.localSelectedPreset?.id ? this.localSelectedPreset : {}

        const cleanFilters = this.currentAd.detailsQuery?.query?.filters?.filter(filter => filter?.[0]?.key !== 'ad_id')
        const cleanKpis = Array.from(
          new Set(
            (this.currentAd.detailsQuery?.query?.selectedColumns || []).map(kpi => kpi.key)
          )
        )
        const creativeQuery = {
          date: this.currentAd.detailsQuery?.date,
          query: {
            ...this.currentAd.detailsQuery?.query,
            filters: cleanFilters,
            selectedColumns: cleanKpis
          },
          creativeHash: this.currentAd.creative_hash,
          preset,
          is_public: isPublic
        }

        const { data: shareRes } = await LensAPI.AdGroups.createShareLink({
          provider: providerKeyMap[this.getMetricsPlatform],
          lensId: this.$route.params.lensId,
          creativeQuery,
          isPublic
        })
        return process.env.VUE_APP_BASE_URL + '/share/lens-creative/' + shareRes?.id
      })
        .then(() => {
          this.$showAlert({
            message: 'Copied share link to clipboard!',
            type: 'success'
          })
        })
        .catch((error) => {
          console.error(error)
          this.$showAlert({
            message: 'Error generating share link',
            type: 'error'
          })
        })
        .finally(() => {
          this.loadingCopyLink = false
        })
    },
    async handleLensAdSave () {
      this.loadingLensAdSave = true
      try {
        const payload = {
          board_ids: [],
          name: this.brand?.name || 'Lens Ad',
          createdAt: new Date().getTime(),
          created_by: this.getUser?.user_id,
          type: this.currentAd.ad_type,
          save_source: 'LENS_CREATIVE',
          ad_id: this.currentAd.ad_id,
          avatar: this.brand?.avatar,
          cta_text: this.currentAd?.cta_text || '',
          cta_type: this.currentAd?.cta_type || '',
          description: this.currentAd?.description || ''
        }
        // TODO @Sam: transcription & ai analytics

        if (this.currentAd.ad_type === 'video') {
          payload.video = this.currentAd.video
        } else if (this.currentAd.ad_type === 'image') {
          payload.image = this.currentAd.image
        }

        if (this.getTeam) {
          payload.teamId = this.getTeam.id
        }

        await FirebaseAPI.Advertisements.create(payload)
        this.$showAlert({
          message: 'Ad saved to Swipe File',
          type: 'success'
        })
      } catch (error) {
        console.error(error)
        this.$showAlert({
          message: 'Error saving ad',
          type: 'error'
        })
      } finally {
        this.loadingLensAdSave = false
      }
    },
    // ================================================================================
    // ================================== UI METHODS ==================================
    // ================================================================================

    showIntercom () {
      window.Intercom('show')
    },
    determineAdCardOverflow () {
      if (this.mobileView) {
        this.enableAdCardScroll = true
        return
      }
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const cardArea = this.$refs['advertisement-card-area']
          const card = this.$refs['advertisement-card']?.$el
          if (cardArea && card) {
            // Subtract 48px for padding and 24px for vert center offset
            const scrollThreshold = cardArea.getBoundingClientRect().height - 48 - 24
            const cardHeight = card.getBoundingClientRect().height
            this.enableAdCardScroll = cardHeight > scrollThreshold
          }
        })
      })
    },
    getDownloadLabel () {
      if (!this.currentAd.type) return 'Download'
      switch (this.currentAd.type) {
        case 'carousel': return 'Current Card'
        case 'dco': return 'Current Variant'
        default: return this.currentAd.type
      }
    },
    getAltDownloadLabel () {
      if (!this.currentAd.type) return 'Alt'
      switch (this.currentAd.type) {
        case 'video': return 'Thumbnail'
        default: return 'Collection'
      }
    },
    formatCreativeId (creativeId) {
      return creativeId.replace('tik_', '')
    },

    // ================================================================================
    // ================================ EVENT HANDLERS ================================
    // ================================================================================

    handleBreakpointChange (event) {
      this.mobileView = event.matches
      this.$nextTick(() => { this.determineAdCardOverflow() })
    },
    // Handle Ad Share Link Copy
    handleClipboardCopy () {
      this.$showAlert({
        message: 'Copied share link to clipboard!',
        type: 'success'
      })
    },
    playAdVideoAtTimestamp (time) {
      const adCard = this.$refs['advertisement-card']
      if (!adCard) return
      adCard.playVideoAtTimestamp(time)
    },
    onVideoMetadataLoaded () {
      const video = this.$refs.video
      const { duration, videoWidth, videoHeight } = video
      this.assetMetadata = { duration, width: videoWidth, height: videoHeight }
      video.pause()
      video.src = ''
      video.load() // Reinitializing load without source will stop any ongoing download
    },
    handleBoardsDropdownOpened (isOpen) {
      this.SET_PREVENT_DRAWER_ESCAPE_KEY(isOpen)
    },
    // Handle Delete Advertisement
    async handleDeleteAdvertisement () {
    },
    forwardTagEdits (tagId, updatedTag) {
      this.$emit('update:tableViewTags', tagId, updatedTag)
    }
  }
}

const convertBreakdownMetricsToFlatArray = (breakdownMetrics) => {
  const flatArray = []
  Object.keys(breakdownMetrics).forEach(key => {
    const breakdown = breakdownMetrics[key]
    if (breakdown.left && breakdown.right) {
      flatArray.push(breakdown.left, breakdown.right)
    } else {
      flatArray.push(breakdown)
    }
  })
  return flatArray
}
</script>

<style scoped>
.detail-section-container{
  box-shadow: 0px 0px 0.75px 0.75px #06070F0A,
              0px 1.5px 1.5px -0.75px #06070F0A,
              0px 3px 3px -1.5px #06070F0A;
}

@property --share-btn-opacity-1 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}
@property --share-btn-opacity-2 {
  syntax: '<number>';
  initial-value: 0.12;
  inherits: false;
}

.info-card {
  box-shadow: 0px 3px 3px -1.5px rgba(6, 7, 16, 0.04), 0px 1.5px 1.5px -0.75px rgba(6, 7, 16, 0.06), 0px 0px 0.25px 0.75px rgba(6, 7, 16, 0.06);
}
.actions-shadow {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.09), 0px 0px 0px 1px rgba(0, 56, 108, 0.04);
}
.center-toolbar-actions {
  position: absolute;
  left: 50%;
  top: calc(50% + 20px);
  transform: translate(-50%, -50%);
  max-width: calc(100vw - 664px);
}
.center-toolbar-actions .action-btn:disabled {
  cursor: default;
  background-color: #F6F8FA;
}
.action-btn.enabled {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: linear-gradient(180deg, rgba(255, 255, 255, var(--share-btn-opacity-1)) 0%, rgba(255, 255, 255, var(--share-btn-opacity-2)) 100%), #006BFF;
  box-shadow: 0px -1px 12px 0px rgba(255, 255, 255, 0.12) inset, 0px 0px 0px 1px #0063F4;
  transition: --share-btn-opacity-1 150ms ease-in-out, --share-btn-opacity-2 150ms ease-in-out;
  transform: scale(0.95);
  transform-origin: center;
}
.action-btn.enabled:hover {
  --share-btn-opacity-1: 0.24;
  --share-btn-opacity-2: 0;
}
.action-btn.enabled:active {
  --share-btn-opacity-1: 0;
  --share-btn-opacity-2: 0.24;
}
.centered-card-wrapper {
  position: absolute;
  top: calc(50% - 20px);
  left: 50%;
  transform: translate(-50%, -50%);
}
.details-organization-grid {
  display: grid;
  grid-template-columns: 136px auto;
  max-width: 100%;
}

.gray-background{
  background-image: url('../../../assets/images/gray-striped-background.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.icon-shadow {
  box-shadow: 0px 1px 2px rgba(9, 25, 72, 0.13), 0px 0px 0px 1px rgba(18, 55, 105, 0.08);
  border-radius: 999px;
}

.scrollable-section {
  overflow-y: auto;
}
.scrollable-section::-webkit-scrollbar {
  width: 3px;
}
.scrollable-section::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 10000px;
}

.download-dropdown {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  border-radius: 10px;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.open-meta-btn {
  background-color: white;
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.13), 0 0 0 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}
.open-meta-btn:hover, .open-meta-btn.active {
  box-shadow: 0 1px 2px 0 rgba(4, 26, 75, 0.17), 0 0 0 1px rgba(0, 56, 108, 0.12);
}

.coming-soon-overlay{
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
}

.fp-metric-tooltip {
  position: absolute;
  left: calc(100% + 6px);
  top: 50%;
  transform: translateY(-50%);
  max-width: 205px;
  z-index: 100;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.tooltip-container{
  pointer-events: none;
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%) translateY(0);
  opacity: 0;
}

.tooltip-container-bottom{
  pointer-events: none;
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%) translateY(0);
  opacity: 0;
  z-index: 100
}

.tooltip-trigger:hover + .tooltip-container,
.tooltip-trigger:hover + .tooltip-container-bottom {
  opacity: 1;
}

.account-name {
  /* If we decide to make it wrap once before truncating: */
  /* display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.account-id {
  font-family: system-ui, Arial, sans-serif;
  font-size: 14px;
  /* Truncate from the left so the ellipsis appears on the left side. */
  direction: rtl;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Keep the actual characters in their natural left-to-right order
   while the rtl container clips/ellipsizes on the left. */
.account-id > span {
  unicode-bidi: plaintext;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fadeDrop-enter-active,
.fadeDrop-leave-active {
  transition: all 150ms;
}

.fadeDrop-enter,
.fadeDrop-leave-to .fadeDrop-leave-active {
  opacity: 0;
  transform: translateY(-1rem);
}
</style>
